import * as React from 'react';
import { CoauthorGallery as CoauthorGalleryBase, CoauthorInformation, CoauthorGalleryStyles } from '@ms/acui-collab';
import {
  FocusZone,
  FocusZoneDirection,
  FocusZoneTabbableElements,
  IContextualMenuStyles,
  mergeStyles
} from '@fluentui/react';
import { componentsIconName } from '@fluidx/icons';
import { coauthorGalleryDataAutomationType } from '../../utilities/testUtilities/testConstants';
import { getSetting } from '@fluidx/utilities';
import { AppButtonOnExecuteParameter, AppButtonProps } from '@ms/acui-button';
import { InputMethod } from '@ms/acui-common';

export interface CoauthorGalleryProps {
  coauthors: CoauthorInformation[];

  overflowMenuHeaderTextString: (placeholders: { count: number }) => string;

  styles?: CoauthorGalleryStyles;

  maxFacepileCoauthors?: number;

  followUser?: (userId: string) => void;

  stopFollowingUser?: (actionHubUserId: string) => void;

  galleryId?: string;

  followButtonText: (placeholders: { userName: string }) => string;

  followExitButtonText: (placeholders: { userName: string }) => string;
}

export { CoauthorInformation } from '@ms/acui-collab';

const focusZoneWrapperDivStyles = mergeStyles({
  float: 'left',
  position: 'relative',
  right: 4,
  top: 4
});

// Unfortunately, the fields are not optional and we need to declare all of them, even if they are empty.
const defaultOverflowMenuStyles: IContextualMenuStyles = {
  root: {},
  container: {},
  list: {},
  title: {},
  header: {},
  subComponentStyles: {
    callout: {},
    menuItem: {
      item: {
        // fluent-ui doesn't expose this in a prettier way, so in order to remove the double scrollbars,
        // we need to target the list through selectors.
        selectors: {
          ['ul']: {
            overflow: 'visible'
          }
        }
      }
    }
  }
};

export const CoauthorGallery = React.memo(function CoauthorGallery(props: CoauthorGalleryProps) {
  const {
    overflowMenuHeaderTextString,
    coauthors,
    styles,
    maxFacepileCoauthors,
    followUser,
    stopFollowingUser,
    galleryId,
    followButtonText,
    followExitButtonText
  } = props;

  const coauthorGalleryStyles: CoauthorGalleryStyles = {
    overflowMenuStyles: { ...defaultOverflowMenuStyles, ...styles?.overflowMenuStyles },
    ...styles
  };

  const getActionHubCommandsToRender = (coauthor: CoauthorInformation, _index: number): AppButtonProps[] => {
    const coauthorName = coauthor.name;
    let coauthorId = coauthor.id;
    // The gallery id is a v4 UUID added to the beginning of each coauthor id by FluidLeap to ensure they are unique per gallery.
    // Removing it here since it is not truly part of the coauthor's user id and the gallery id is not of any relevance in this context.
    if (galleryId !== undefined) {
      coauthorId = coauthorId.replace(galleryId, '');
    }
    return [
      {
        label: followButtonText({ userName: coauthorName }),
        id: 'follow-me',
        onExecute: (parameter: AppButtonOnExecuteParameter) => {
          if (isButtonInputTypeValid(parameter.inputMethod) && followUser !== undefined) {
            followUser(coauthorId);
          }
        },
        type: 'AppButtonProps'
      },
      {
        label: followExitButtonText({ userName: coauthorName }),
        id: 'stop-follow-me',
        onExecute: (parameter: AppButtonOnExecuteParameter) => {
          if (isButtonInputTypeValid(parameter.inputMethod) && stopFollowingUser !== undefined) {
            stopFollowingUser(coauthorId);
          }
        },
        type: 'AppButtonProps'
      }
    ];
  };

  const followMeEnabled = getSetting('container.liveCursorPresence' /* settingName */, false /* defaultValue */);

  return (
    <div className={focusZoneWrapperDivStyles}>
      <FocusZone direction={FocusZoneDirection.horizontal} handleTabKey={FocusZoneTabbableElements.none}>
        <CoauthorGalleryBase
          dataAutomationType={coauthorGalleryDataAutomationType}
          type="CoauthorGalleryProps"
          coauthors={coauthors}
          maxFacepileCoauthors={maxFacepileCoauthors ?? 8} // By default have a maximum of 8 coauthors
          styles={coauthorGalleryStyles}
          overflowMenuHeaderText={overflowMenuHeaderTextString({ count: coauthors.length })}
          smallFallBackUserImage={componentsIconName('FFXCPawn')}
          largeFallBackUserImage={componentsIconName('FFXCPawn')}
          getActionHubCommandsToRender={followMeEnabled ? getActionHubCommandsToRender : undefined}
        />
      </FocusZone>
    </div>
  );
});

const isButtonInputTypeValid = (inputMethod: InputMethod | undefined): boolean => {
  return (
    inputMethod !== undefined &&
    inputMethod !== InputMethod.NotDefined &&
    inputMethod !== InputMethod.Unknown &&
    inputMethod !== InputMethod.CrossSlide
  );
};

// cSpell:ignore fluentui acui facepile ffxc tabbable
